"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Lightbox } from "@/components/gallery/lightbox";
import { PROPERTY_SECTIONS, GALLERY_IMAGES } from "@/lib/constants";

export function PhotoGrid() {
  const [activeTab, setActiveTab] = useState<string>(PROPERTY_SECTIONS[0].id);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const allImages = useMemo(
    () => Object.values(GALLERY_IMAGES).flat(),
    []
  );

  const currentImages = showAll
    ? allImages
    : (GALLERY_IMAGES[activeTab] || []);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <section id="gallery" className="section-padding bg-card/30">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">
            Explore
          </p>
          <h2 className="mt-3 font-playfair text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
            Photo Gallery
          </h2>
        </motion.div>

        {/* Category Tabs */}
        <div className="mt-10">
          <Tabs
            value={showAll ? "__all__" : activeTab}
            onValueChange={(val) => {
              if (val === "__all__") {
                setShowAll(true);
              } else {
                setShowAll(false);
                setActiveTab(val);
              }
            }}
          >
            <TabsList
              variant="line"
              className="mx-auto flex max-w-full flex-wrap justify-center gap-1"
            >
              {PROPERTY_SECTIONS.map((section) => (
                <TabsTrigger key={section.id} value={section.id}>
                  {section.label}
                </TabsTrigger>
              ))}
              <TabsTrigger value="__all__">All Photos</TabsTrigger>
            </TabsList>

            {/* Grid — single content area, images driven by state */}
            <div className="mt-8">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 md:gap-4">
                {currentImages.map((img, i) => {
                  const isLarge = i === 0 || i === 3;
                  return (
                    <motion.div
                      key={`${img.src}-${i}`}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: (i % 8) * 0.05 }}
                      className={`group relative cursor-pointer overflow-hidden rounded-xl ${
                        isLarge
                          ? "col-span-2 row-span-2 aspect-[4/3]"
                          : "aspect-square"
                      }`}
                      onClick={() => openLightbox(i)}
                    >
                      <Image
                        src={img.src}
                        alt={img.alt}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-110"
                        sizes={
                          isLarge
                            ? "(max-width: 768px) 100vw, 50vw"
                            : "(max-width: 768px) 50vw, 25vw"
                        }
                      />
                      <div className="absolute inset-0 bg-black/0 transition-all duration-300 group-hover:bg-black/30" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        <Camera className="size-8 text-white" />
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {!showAll && (
                <div className="mt-8 text-center">
                  <Button
                    variant="outline"
                    size="lg"
                    className="rounded-full border-primary/40 text-primary hover:border-primary hover:bg-primary/10"
                    onClick={() => setShowAll(true)}
                  >
                    <Camera className="mr-2 size-4" />
                    View All Photos ({allImages.length})
                  </Button>
                </div>
              )}
            </div>
          </Tabs>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={currentImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNavigate={setLightboxIndex}
        />
      )}
    </section>
  );
}
